<div class="container">
  <div class="key-fields-header">
    <div class="head-label">
      <span>{{ 'documentFields' | translate }}</span>
    </div>
    @if (userHasModified()) {
      <div
        class="save-status"
        role="status"
        [ngClass]="{
          saving: isSaving(),
          'up-to-date': !isSaving() && activeForm().pristine && !saveFailed(),
          'save-failed': saveFailed()
        }"
      >
        <i
          class="fa-solid"
          aria-hidden="true"
          [ngClass]="{
            'fa-spinner-scale fa-spin-pulse': isSaving() || !activeForm().pristine,
            'fa-circle-check': !isSaving() && !saveFailed(),
            'fa-circle-exclamation': saveFailed()
          }"
        ></i>
        {{
          isSaving()
            ? ('savingChanges' | translate)
            : saveFailed()
              ? 'Failed to Save'
              : !activeForm().pristine
                ? ('changesDetected' | translate)
                : ('allChangesSaved' | translate)
        }}
      </div>
    }
  </div>
  @if (!currentDocument().completed) {
    @if (!autoclassificationEnabled || documentIsAvailable()) {
      <form
        class="key-fields-form"
        [formGroup]="activeForm()"
        (ngSubmit)="exportDocument()"
        (keydown.enter)="$event.preventDefault()"
      >
        <div class="key-fields-container">
          <div class="input-container area">
            <label
              class="input-label"
              for="areaSelector"
              >{{ 'area' | translate }}</label
            >
            <p-select
              placeholder="{{ 'defaultAreaPlaceholder' | translate }}"
              class="dap-select-area input-field"
              formControlName="area"
              id="areaSelector"
              optionLabel="name"
              optionValue="code"
              filterBy="name"
              [attr.aria-required]="true"
              [options]="docAreas"
              [filter]="true"
              [appendTo]="'body'"
              (onChange)="onAreaUpdate($event)"
            ></p-select>
          </div>
          @if (activeForm().get('area')?.value) {
            <div class="input-container type">
              <label
                class="input-label"
                for="documentTypeSelector"
                >{{ 'documentType' | translate }}</label
              >
              <p-select
                class="dap-select-doc-type input-field"
                optionLabel="name"
                optionValue="code"
                filterBy="name"
                formControlName="documentTypeCode"
                id="documentTypeSelector"
                placeholder="Select a document type"
                [options]="filteredDocTypes()"
                [filter]="true"
                [appendTo]="'body'"
                (onChange)="onDocTypeUpdate($event)"
              >
              </p-select>
            </div>
          }
          @if (activeKeyfields().length > 0) {
            @for (keyfield of activeKeyfields(); track keyfield.code) {
              <div class="input-container">
                <label
                  class="input-label"
                  [for]="keyfield.code"
                  >{{ keyfield.name | translate }}</label
                >
                @switch (keyfield.dataType) {
                  @case ('Lookup') {
                    <p-autocomplete
                      #autoComplete
                      #Lookup
                      optionLabel="name"
                      class="dap-lookup-field"
                      dropdownMode="current"
                      [appendTo]="'body'"
                      [formControlName]="keyfield.code"
                      [ngClass]="{ 'input-required': activeForm().get(keyfield.code)?.errors }"
                      [suggestions]="activeLookup()"
                      [forceSelection]="true"
                      [showClear]="true"
                      [dropdown]="true"
                      (completeMethod)="searchLookup($event, keyfield)"
                      (onSelect)="onFieldChange()"
                      (onClear)="onFieldChange()"
                    />
                  }
                  @case ('Date') {
                    <p-datepicker
                      formControlName="{{ keyfield.code }}"
                      placeholder="mm/dd/yyyy"
                      class="dap-datepicker-field"
                      [ngClass]="{ 'input-required': activeForm().get(keyfield.code)?.errors }"
                      [showIcon]="true"
                      (onSelect)="onFieldChange()"
                      (onInput)="onFieldChange()"
                    >
                    </p-datepicker>
                  }
                  @case ('Text')
                  @case ('Decimal') {
                    @if (keyfield.constraint) {
                      <p-select
                        optionLabel="label"
                        class="dap-select-field"
                        optionValue="value"
                        [formControlName]="keyfield.code"
                        [options]="
                          keyfield.constraint
                            .filter(c => c != null)
                            .map(c => ({ label: c, value: c }))
                        "
                        [placeholder]="'selectOption' | translate"
                        (onChange)="onFieldChange()"
                      >
                      </p-select>
                    } @else if (keyfield.multivalue) {
                      <div class="multiple-value-container">
                        <input
                          class="dap-multi-value-field"
                          [formControlName]="keyfield.code"
                          (keydown.enter)="updateMultivalueField(keyfield.code)"
                          (input)="onFieldChange()"
                        />
                        @for (
                          value of multiValueFields().get(
                            `${activeAreaCode()}-${activeDoctype().code}-${keyfield.code}`
                          );
                          track $index;
                          let i = $index
                        ) {
                          <div class="multiple-option-container">
                            <p-button
                              class="dap-remove-value-btn"
                              icon="fa-regular fa-xmark"
                              severity="danger"
                              variant="text"
                              size="small"
                              [attr.aria-label]="('removeValue' | translate) + ': ' + value"
                              (click)="removeMultipleValue(keyfield.code, i)"
                            >
                            </p-button>
                            <span class="value"> {{ value }} </span>
                          </div>
                        }
                      </div>
                    } @else if (keyfield.multiline) {
                      <textarea
                        pTextarea
                        class="dap-text-area-field"
                        [formControlName]="keyfield.code"
                        [ngClass]="{ 'input-required': activeForm().get(keyfield.code)?.errors }"
                        (input)="onFieldChange()"
                      >
                      </textarea>
                    } @else {
                      <input
                        type="text"
                        class="dap-input-field"
                        pInputText
                        maxFractionDigits="20"
                        [formControlName]="keyfield.code"
                        [ngClass]="{ 'input-required': activeForm().get(keyfield.code)?.errors }"
                        (input)="onFieldChange()"
                      />
                    }
                  }
                  @case ('Number') {
                    <p-inputNumber
                      type="number"
                      class="dap-number-field"
                      [formControlName]="keyfield.code"
                      [ngClass]="{ 'input-required': activeForm().get(keyfield.code)?.errors }"
                      (onInput)="onFieldChange()"
                    />
                  }
                  @case ('Money') {
                    <p-inputNumber
                      type="text"
                      class="dap-money-field"
                      mode="currency"
                      currency="USD"
                      [formControlName]="keyfield.code"
                      [ngClass]="{ 'input-required': activeForm().get(keyfield.code)?.errors }"
                      (blur)="transformToCurrency($event)"
                      (onInput)="onFieldChange()"
                    />
                  }
                }
                @if (activeForm().get(keyfield.code)?.errors) {
                  <span
                    class="error-label"
                    role="alert"
                    >{{ errorText(keyfield.code) }}</span
                  >
                }
              </div>
            }
          }
        </div>
        @if (canFile) {
          <div class="button-container">
            @if (showDeleteDocument) {
              <p-button
                class="dap-delete-doc-btn delete-document-button"
                icon="fa-solid fa-trash"
                severity="danger"
                text="true"
                size="large"
                [attr.aria-label]="'deleteDocument' | translate"
                [disabled]="isSaving()"
                (click)="deleteDocumentEvent.emit()"
              ></p-button>
            }
            <p-button
              label="{{ 'send' | translate }}"
              class="dap-send-to-content-btn"
              type="submit"
              severity="secondary"
              size="large"
              [disabled]="
                !activeForm().controls['area'].value ||
                !activeForm().controls['documentTypeCode'].value ||
                isSaving() ||
                !activeForm().pristine ||
                !activeForm().valid
              "
              [attr.aria-label]="'send' | translate"
            ></p-button>
          </div>
        }
      </form>
    }
  } @else {
    <div class="centered-button-container">
      <p
        class="success-message"
        role="status"
      >
        {{ 'documentUploadedMessage' | translate }}
      </p>
      <a
        target="_blank"
        rel="noopener noreferrer"
        class="dap-go-to-doc-btn p-button content-url-button"
        [href]="dynamicUrl()"
        [attr.aria-label]="('goToDocument' | translate) + ' (opens in new tab)'"
      >
        {{ 'goToDocument' | translate }}
      </a>
    </div>
  }
  @if (!documentIsAvailable() && autoclassificationEnabled) {
    <div
      class="spinner-container"
      role="status"
      aria-live="polite"
    >
      <i class="fa-solid fa-spinner fa-pulse"></i>
      <span class="spinner-label"> {{ 'classificationInProgress' | translate }} </span>
      <div class="cancel-classification-container">
        <p-button
          variant="outlined"
          severity="secondary"
          class="dap-cancel-extraction-btn cancel-autoclassify-button"
          [label]="'cancel' | translate"
          [attr.aria-label]="'cancel' | translate"
          (click)="cancelClassification()"
        >
        </p-button>
      </div>
    </div>
  }
</div>
